import { ErrorResponse, PointLog, ServiceResponse } from '@bzbs/react-api-client';
import { create } from 'zustand';
import { useBuzzebeesAppStore } from './buzzebeess-app.store';
import { useLocaleStore } from './locale.store';

export interface PointLogConfig {
  defaultPageSize?: number;
  type: 'earn' | 'burn';
}

export type PointLogState = {
  data: PointLog[];
  isLoading: boolean;
  hasMore: boolean;
  error: ErrorResponse | null;
  config: PointLogConfig;
  lastRowKey?: string;
  currentMonth?: string;
};

export type PointLogActions = {
  fetchData: (month?: string) => Promise<ServiceResponse<PointLog[]>>;
  loadMore: () => Promise<ServiceResponse<PointLog[]>>;
  clear: () => void;
  updateConfig: (config: Partial<PointLogConfig>) => void;
};

export type PointLogStore = PointLogState & PointLogActions;

export const createPointLogStore = (initialConfig: PointLogConfig) => {
  return create<PointLogStore>((set, get) => ({
    data: [],
    isLoading: false,
    hasMore: true,
    error: null,
    lastRowKey: undefined,
    currentMonth: undefined,
    config: {
      ...initialConfig,
      defaultPageSize: initialConfig.defaultPageSize || 10,
    },

    updateConfig: (newConfig: Partial<PointLogConfig>) => {
      set((state) => ({
        config: { ...state.config, ...newConfig },
      }));
    },

    fetchData: async (month?: string) => {
      const { config } = get();
      const localeId = useLocaleStore.getState().localeId;
      const bzbsService = useBuzzebeesAppStore.getState().bzbsService;

      if (!bzbsService) {
        return { type: 'client-error', message: 'Service not configured' };
      }

      const targetMonth = month || new Date().toISOString().split('T')[0];

      set({ isLoading: true });
      const response = await bzbsService.pointLogApi.getPointLog({
        month: targetMonth,
        type: config.type,
        top: config.defaultPageSize,
        options: { device_locale: localeId },
      });
      set({ isLoading: false });

      if (response.type === 'success') {
        const lastItem = response.model[response.model.length - 1];
        set({
          data: response.model,
          hasMore: response.model.length === config.defaultPageSize,
          error: null,
          lastRowKey: lastItem?.RowKey,
          currentMonth: targetMonth,
        });
      } else {
        set({ error: response });
      }

      return response;
    },

    loadMore: async () => {
      const { config, data, lastRowKey, currentMonth } = get();
      const localeId = useLocaleStore.getState().localeId;
      const bzbsService = useBuzzebeesAppStore.getState().bzbsService;

      if (!bzbsService) {
        return { type: 'client-error', message: 'Service not configured' };
      }

      if (!lastRowKey) {
        return { type: 'client-error', message: 'No more data to load' };
      }

      if (!currentMonth) {
        return { type: 'client-error', message: 'No month specified, call fetchData first' };
      }

      set({ isLoading: true });
      const response = await bzbsService.pointLogApi.getPointLog({
        month: currentMonth,
        type: config.type,
        top: config.defaultPageSize,
        lastRowKey: lastRowKey,
        options: { device_locale: localeId },
      });
      set({ isLoading: false });

      if (response.type === 'success') {
        const lastItem = response.model[response.model.length - 1];
        set({
          data: [...data, ...response.model],
          hasMore: response.model.length === config.defaultPageSize,
          error: null,
          lastRowKey: lastItem?.RowKey,
        });
      } else {
        set({ error: response });
      }

      return response;
    },

    clear: () => {
      set({
        data: [],
        isLoading: false,
        hasMore: true,
        error: null,
        lastRowKey: undefined,
        currentMonth: undefined,
      });
    },
  }));
};
